"use client";

import React from "react";
import { Activity, CircleAlert as AlertCircle, CircleCheck as CheckCircle2, Clock, Coffee, HeartPulse, Info, Pill, Shirt, Wine, ZapOff } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Separator } from "@/components/ui/separator";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
interface GuidanceItem {
  icon: React.ComponentType<{
    className?: string;
  }>;
  text: string;
}
const vo2MaxGuidelines: GuidanceItem[] = [{
  icon: ZapOff,
  text: "Avoid strenuous exercise or endurance training during the 24 hours before testing."
}, {
  icon: Clock,
  text: "Avoid eating for at least 3 hours before testing."
}, {
  icon: Coffee,
  text: "Avoid caffeine for at least 3 hours before testing."
}, {
  icon: Wine,
  text: "Avoid alcohol for at least 3 hours before testing."
}, {
  icon: AlertCircle,
  text: "Avoid tobacco or nicotine for at least 3 hours before testing."
}, {
  icon: CheckCircle2,
  text: "Arrive appropriately hydrated."
}, {
  icon: Shirt,
  text: "Wear comfortable exercise clothing and appropriate footwear."
}, {
  icon: Pill,
  text: "Continue prescribed medication unless your own healthcare professional has told you otherwise."
}];
export default function TestPreparationGuidance() {
  const {
    ref: sectionRef,
    isVisible
  } = useRevealOnScroll<HTMLElement>({
    threshold: 0.15
  });
  return <section id="testpreparationguidance" data-nav="light" ref={sectionRef} className="bg-background text-foreground py-16 sm:py-20 lg:py-24 border-b border-border transition-opacity duration-700 ease-out" style={{
    opacity: isVisible ? 1 : 0
  }}>
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        <header className="max-w-3xl mb-12 sm:mb-16">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight text-foreground">
            Test preparation guidance
          </h2>
          <p className="mt-4 text-base sm:text-lg text-muted-foreground leading-relaxed">
            Follow these general guidelines ahead of your metabolic and performance assessments to ensure valid, reproducible clinical data.
          </p>
        </header>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-10 items-start">
          {/* Sub-section A */}
          <div className="lg:col-span-7">
            <Card className="border border-border bg-card shadow-xs">
              <CardHeader className="pb-4">
                <div className="flex items-center gap-3">
                  <div className="size-10 rounded-lg bg-primary/10 text-primary flex items-center justify-center">
                    <Activity className="size-5" />
                  </div>
                  <div>
                    <CardTitle className="text-xl sm:text-2xl font-semibold text-card-foreground">
                      How to prepare for your VO₂ Max test
                    </CardTitle>
                    <CardDescription className="text-sm text-muted-foreground mt-1">
                      KORR-aligned protocol instructions to ensure accurate aerobic capacity data
                    </CardDescription>
                  </div>
                </div>
              </CardHeader>

              <Separator className="my-2" />

              <CardContent className="pt-6 space-y-4">
                <ul className="space-y-3.5" role="list">
                  {vo2MaxGuidelines.map((item, index) => {
                  const IconComponent = item.icon;
                  return <li key={index} data-index={index} className="flex items-start gap-3.5 text-sm sm:text-base text-foreground">
                        <div className="mt-0.5 rounded-lg bg-secondary p-1 text-primary shrink-0">
                          <IconComponent className="size-4" />
                        </div>
                        <span className="leading-snug">{item.text}</span>
                      </li>;
                })}
                </ul>

                <Alert className="mt-6 bg-secondary/50 border-border text-foreground">
                  <Info className="size-4 text-primary" />
                  <AlertTitle className="font-semibold text-foreground">
                    Appointment notification
                  </AlertTitle>
                  <AlertDescription className="text-muted-foreground text-sm leading-relaxed mt-1">
                    Specific preparation instructions will be provided with your appointment. Follow those instructions if they differ from this general guidance.
                  </AlertDescription>
                </Alert>
              </CardContent>
            </Card>
          </div>

          {/* Sub-section B */}
          <div className="lg:col-span-5">
            <Card className="border border-border bg-card shadow-xs">
              <CardHeader className="pb-4">
                <div className="flex items-center gap-3">
                  <div className="size-10 rounded-lg bg-primary/10 text-primary flex items-center justify-center">
                    <HeartPulse className="size-5" />
                  </div>
                  <div>
                    <CardTitle className="text-xl sm:text-2xl font-semibold text-card-foreground">
                      Preparing for an accurate resting measurement
                    </CardTitle>
                    <CardDescription className="text-sm text-muted-foreground mt-1">
                      Factors influencing resting metabolic rate (RMR)
                    </CardDescription>
                  </div>
                </div>
              </CardHeader>

              <Separator className="my-2" />

              <CardContent className="pt-6 space-y-5">
                <p className="text-sm sm:text-base text-muted-foreground leading-relaxed">
                  Resting metabolic rate (RMR) reflects baseline energy expenditure under steady-state conditions. Results can be significantly affected by recent food intake, strenuous exercise, caffeine, stimulants, and physical activity prior to testing.
                </p>

                <p className="text-sm sm:text-base text-muted-foreground leading-relaxed">
                  Because individuals metabolize nutrients and clear stimulants at varying rates, individualized guidance is issued in advance rather than applying an inflexible routine.
                </p>

                <Alert className="bg-secondary/50 border-border text-foreground">
                  <Info className="size-4 text-primary" />
                  <AlertTitle className="font-semibold text-foreground">
                    Pre-assessment instructions
                  </AlertTitle>
                  <AlertDescription className="text-muted-foreground text-sm leading-relaxed mt-1">
                    Detailed preparation instructions will be sent before the appointment and should be followed carefully to improve measurement consistency.
                  </AlertDescription>
                </Alert>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </section>;
}